import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { CloseIcon } from '@components/icons';
import { StudyGroupList } from './study-groups/study-group-list';
import { StudentList } from './students/student-list';

/**
 * Groups & Students settings modal — opened from the study panel gear
 * button. Open state lives in the store so guided-tour steps can drive it.
 * Portaled to document.body: modals are application-modal, and a panel
 * ancestor's backdrop-filter would otherwise contain the fixed overlay.
 */
export const StudySettingsModal = observer(function StudySettingsModal() {
  if (!bibleState.studySettingsOpen) return null;
  if (typeof document === 'undefined') return null;

  const activeTab = bibleState.studySettingsTab;
  const onClose = () => bibleState.closeStudySettings();

  // Only while the tour's CURRENT step works inside this modal does it
  // render scrimless and click-through around the card, so the tour's
  // step card and nav stay visible and operable (nothing outranks the
  // modal layer). Any other open — including a manual gear-open while
  // the tour idles on another step — keeps the full application-modal dim.
  const isTourDriven = bibleState.guideDrivesSettings;

  return createPortal(
    <div
      className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isTourDriven ? 'pointer-events-none' : ''}`}
    >
      {/* Backdrop — omitted during the tour */}
      {!isTourDriven && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      )}

      {/* Modal — fixed proportions (clamped for short windows) so the
          card never jumps height as tab content changes; body scrolls */}
      <div className="pointer-events-auto relative flex h-[min(44rem,85vh)] w-full max-w-xl flex-col rounded-2xl bg-white p-6 shadow-xl dark:bg-stone-800">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
            Groups & Students
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-stone-400 transition-colors hover:text-stone-600 dark:hover:text-stone-300"
            title="Close"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex gap-1 rounded-xl bg-stone-100 p-1 dark:bg-stone-900/40">
          <button
            onClick={() => bibleState.setStudySettingsTab('groups')}
            className={`flex-1 rounded-lg py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'groups'
                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                : 'text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200'
            }`}
          >
            Groups
          </button>
          <button
            onClick={() => bibleState.setStudySettingsTab('students')}
            className={`flex-1 rounded-lg py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'students'
                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                : 'text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200'
            }`}
          >
            Students
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {activeTab === 'groups' && <StudyGroupList />}
          {activeTab === 'students' && <StudentList />}
        </div>
      </div>
    </div>,
    document.body,
  );
});

export default StudySettingsModal;
